/**
 * Processing pipeline stages, mirrored from backend/shared/constants.py:36-41.
 * Order is a hard contract with the backend processor (backend/handlers/processor.py
 * writes these in sequence). If the backend adds/renames a stage without a frontend
 * update, getStageLabel() humanizes the unknown value so the UI stays sensible.
 */
export const PROCESSING_STAGES = [
  'pdf_extraction',
  'pdf_concatenation',
  'llm_pipeline',
  'vendor_matching',
  'completion',
] as const;

export type ProcessingStage = typeof PROCESSING_STAGES[number];

/** Per-step presentation. `icon` is a Bootstrap Icons class (bi bi-*). */
export const STAGE_META: Record<ProcessingStage, { label: string; icon: string }> = {
  pdf_extraction:    { label: 'Reading your report',    icon: 'bi-file-earmark-text' },
  pdf_concatenation: { label: 'Preparing documents',    icon: 'bi-files' },
  llm_pipeline:      { label: 'Analyzing findings',     icon: 'bi-cpu' },
  vendor_matching:   { label: 'Matching contractors',   icon: 'bi-people' },
  completion:        { label: 'Finalizing',             icon: 'bi-check2-circle' },
};

export type StageState = 'completed' | 'current' | 'upcoming';

/** Index of a stage in the ordered list, or -1 if unknown/undefined. */
export function getStageIndex(stage: string | undefined): number {
  if (!stage) return -1;
  return (PROCESSING_STAGES as readonly string[]).indexOf(stage);
}

/**
 * Friendly label for a stage.
 * - Known stage → mapped label.
 * - Unknown non-empty stage → humanized snake_case → Title Case.
 * - undefined/empty → 'Processing'.
 */
export function getStageLabel(stage: string | undefined): string {
  if (!stage) return 'Processing';
  const known = STAGE_META[stage as ProcessingStage];
  if (known) return known.label;
  return stage
    .split('_')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * State of `target` step given the current stage.
 * If currentStage is unknown/undefined (index -1), every step is 'upcoming'.
 */
export function getStageState(
  currentStage: string | undefined,
  target: ProcessingStage,
): StageState {
  const currentIdx = getStageIndex(currentStage);
  const targetIdx = getStageIndex(target);
  if (currentIdx === -1) return 'upcoming';
  if (targetIdx < currentIdx) return 'completed';
  if (targetIdx === currentIdx) return 'current';
  return 'upcoming';
}
